import { useState } from 'react';
import type { Account } from '../types';
import { fmtMoney, relativeDate } from '../utils/format';

interface Touch {
  date: string;
  value: number;
  createdAt: string;
}

interface Props {
  account: Account;
  /** Current (latest explicit) value for this account. */
  value: number;
  share: number;
  color: string;
  /** Only snapshots that explicitly set this account's balance, ascending. */
  touches: Touch[];
  rank: number;
}

export function AccountCard({ account, value, share, color, touches, rank }: Props) {
  // Last explicit touch = the snapshot where this balance was actually entered.
  const lastTouch = touches.at(-1);
  // Prior explicit touch with a different value → use for delta display.
  let prior: Touch | null = null;
  for (let i = touches.length - 2; i >= 0; i--) {
    if (touches[i].value !== value) {
      prior = touches[i];
      break;
    }
  }
  const delta = prior ? value - prior.value : 0;
  const deltaPct = prior && prior.value > 0 ? delta / prior.value : 0;

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-3 dark:bg-ink-800/80 dark:border-ink-700">
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold text-white"
          style={{ backgroundColor: color }}
          aria-hidden
        >
          #{rank}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="truncate text-sm font-semibold text-ink-900 dark:text-ink-50">
              {account.name}
            </div>
            <div className="shrink-0 font-mono text-base font-bold tabular-nums text-ink-900 dark:text-ink-50">
              {fmtMoney(value)}
            </div>
          </div>
          <div className="mt-0.5 flex items-baseline justify-between gap-2 text-[11px]">
            <span className="text-ink-500 dark:text-ink-300">
              {kindLabel(account.kind)} · {(share * 100).toFixed(1)}%
              {lastTouch ? (
                <> · upd {relativeDate(lastTouch.date)}</>
              ) : (
                <> · never updated</>
              )}
            </span>
            {prior && delta !== 0 && (
              <span
                className={`font-mono font-semibold tabular-nums ${
                  delta > 0 ? 'text-pos-700 dark:text-pos-500' : 'text-neg-700 dark:text-neg-500'
                }`}
                title={`vs. ${prior.date}`}
              >
                {delta > 0 ? '+' : '−'}
                {fmtMoney(Math.abs(delta))} ({delta > 0 ? '+' : '−'}
                {(Math.abs(deltaPct) * 100).toFixed(1)}%)
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2.5 flex items-center gap-2">
        <div
          className="h-1.5 rounded-full bg-ink-100 dark:bg-ink-700"
          style={{ width: `${Math.max(3, share * 100)}%` }}
          aria-hidden
        >
          <div className="h-full rounded-full" style={{ width: '100%', backgroundColor: color }} />
        </div>
        {touches.length >= 2 && <Sparkline points={touches} color={color} />}
      </div>
    </div>
  );
}

function kindLabel(k: Account['kind']): string {
  switch (k) {
    case 'hysa': return 'HYSA';
    case 'checking': return 'Checking';
    case 'taxable': return 'Taxable';
    case 'retirement': return 'Retirement';
    case 'home': return 'Home';
    case 'mortgage': return 'Mortgage';
    case 'other_asset': return 'Other asset';
    case 'other_liability': return 'Other liability';
  }
}

/**
 * Time-honest per-account sparkline: x is proportional to the real gap
 * between snapshots, with a dot at every actual sample so sparse or
 * clustered updates read as such rather than as evenly-spaced dashes.
 * Hover snaps to the nearest sample by pixel distance.
 */
function Sparkline({ points, color }: { points: { date: string; value: number }[]; color: string }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  if (points.length < 2) return null;
  const w = 120;
  const h = 24;
  const values = points.map(p => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const times = points.map(p => new Date(p.date).getTime());
  const tMin = times[0];
  const tMax = times[times.length - 1];
  const tSpan = tMax - tMin || 1;
  const xy = points.map((p, i) => ({
    x: ((times[i] - tMin) / tSpan) * w,
    y: h - ((p.value - min) / range) * h,
  }));
  const path = xy.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`).join(' ');
  return (
    <svg
      className="ml-auto h-6 w-28 shrink-0"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      onMouseMove={e => {
        const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
        const rel = ((e.clientX - rect.left) / rect.width) * w;
        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i < xy.length; i++) {
          const d = Math.abs(xy[i].x - rel);
          if (d < bestDist) { bestDist = d; best = i; }
        }
        setHoverIdx(best);
      }}
      onMouseLeave={() => setHoverIdx(null)}
    >
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      {xy.map((pt, i) => (
        <circle key={i} cx={pt.x} cy={pt.y} r={1.25} fill={color} vectorEffect="non-scaling-stroke" />
      ))}
      {hoverIdx != null && (
        <circle cx={xy[hoverIdx].x} cy={xy[hoverIdx].y} r={2.5} fill={color} stroke="#fff" strokeWidth={1} />
      )}
    </svg>
  );
}
